import { AmbienceBed, AudioManifest } from './Manifest';
import { CrossfadeLoop } from './CrossfadeLoop';

/**
 * Dźwięk w grze: muzyka, tło i kroki.
 *
 * Three buses under one master, because that is what the settings offer and
 * because they are three different kinds of thing: music is a mood, ambience is
 * a place, a footstep is an event. Anything that wants to be quieter is quieter
 * on its bus rather than by having its own volume argument threaded through
 * every call.
 *
 * **Nothing plays until the page has been touched.** Browsers suspend an
 * `AudioContext` created without a gesture behind it, and that is not a bug to
 * work around — a game that starts talking before anybody has clicked on it is
 * the reason the rule exists. `unlock` is called from the first click, and
 * everything before it is queued rather than lost.
 *
 * **Ambience beds run continuously at zero.** Starting and stopping a loop as
 * the player walks in and out of a wood would put a seam at exactly the moment
 * somebody is listening for one; two or three sources playing silently cost
 * nothing measurable, and the only thing that moves is a gain.
 */

export type Bus = 'music' | 'ambience' | 'effects';

/** How quickly a bed follows the ground: the time constant of its approach. */
const AMBIENCE_GLIDE = 1.2;

/** How far a footstep may be shifted in pitch and in level. */
const STEP_PITCH = 0.08;
const STEP_LEVEL = 0.15;

interface Bed {
  entry: AmbienceBed;
  gain: GainNode;
  loop?: CrossfadeLoop;
  /** What the ground says it should be, kept for when the sound is unlocked. */
  level: number;
}

export class AudioEngine {
  private readonly ctx: AudioContext;
  private readonly master: GainNode;
  private readonly buses: Record<Bus, GainNode>;

  private readonly beds = new Map<string, Bed>();
  private music?: CrossfadeLoop;
  private steps: AudioBuffer[] = [];
  /** So the same footstep is never heard twice running. */
  private lastStep = -1;
  private chops: AudioBuffer[] = [];
  /** Plac budowy: pętla na szynie tła, głośność z odległości. */
  private work?: Bed;
  /** Z dwoma nagraniami „nigdy to samo dwa razy" znaczy „na przemian". */
  private lastChop = -1;

  private unlocked = false;
  private loaded = false;

  constructor(private readonly base = '/audio') {
    this.ctx = new AudioContext();
    this.master = this.ctx.createGain();
    this.master.connect(this.ctx.destination);

    const bus = (): GainNode => {
      const node = this.ctx.createGain();
      node.connect(this.master);
      return node;
    };
    this.buses = { music: bus(), ambience: bus(), effects: bus() };
  }

  /** What a bus is set to, 0 to 1. */
  volume(bus: Bus): number {
    return this.buses[bus].gain.value;
  }

  /**
   * Turn a bus up or down.
   *
   * Ramped rather than set, because a slider dragged across its track is a
   * stream of values and a gain jumping between them clicks.
   */
  setVolume(bus: Bus, value: number): void {
    const level = Math.min(1, Math.max(0, value));
    this.buses[bus].gain.setTargetAtTime(level, this.ctx.currentTime, 0.02);
  }

  /**
   * Let sound out, once something has been clicked.
   *
   * Safe to call again: the browser may suspend the context when the tab goes
   * to the background, so this is also what the way back in looks like.
   */
  async unlock(): Promise<void> {
    if (this.ctx.state === 'suspended') {
      await this.ctx.resume().catch(() => undefined);
    }
    if (this.unlocked) return;
    this.unlocked = true;
    this.startBeds();
  }

  /**
   * Fetch and decode everything the manifest names.
   *
   * Eagerly, all of it: this is a handful of files and the alternative — a wood
   * that starts being heard some seconds after it comes into view — is worse
   * than the bytes. A file that will not load is skipped with a line in the
   * console; the rest still plays.
   */
  async load(manifest: AudioManifest): Promise<void> {
    const decode = async (file: string): Promise<AudioBuffer | undefined> => {
      try {
        const response = await fetch(`${this.base}/${file}`);
        if (!response.ok) throw new Error(`${response.status}`);
        return await this.ctx.decodeAudioData(await response.arrayBuffer());
      } catch (error) {
        console.warn(`dźwięk: ${file} nie wczytał się`, error);
        return undefined;
      }
    };

    const [music, ambience, steps, chops, work] = await Promise.all([
      // One track for now. A playlist is a different feature and would need to
      // say what follows what; this says what plays.
      manifest.music[0] ? decode(manifest.music[0].file) : Promise.resolve(undefined),
      Promise.all(manifest.ambience.map((entry) => decode(entry.file))),
      Promise.all(manifest.steps.map((entry) => decode(entry.file))),
      Promise.all(manifest.chops.map((entry) => decode(entry.file))),
      Promise.all((manifest.construction?.files ?? []).map((entry) => decode(entry.file))),
    ]);

    if (music) {
      const gain = this.ctx.createGain();
      gain.gain.value = manifest.music[0].gain ?? 1;
      gain.connect(this.buses.music);
      this.music = new CrossfadeLoop(this.ctx, music, gain, 3);
    }

    manifest.ambience.forEach((entry, at) => {
      const buffer = ambience[at];
      if (!buffer) return;
      const gain = this.ctx.createGain();
      gain.gain.value = 0;
      gain.connect(this.buses.ambience);
      this.beds.set(entry.key, {
        entry,
        gain,
        loop: new CrossfadeLoop(this.ctx, buffer, gain, 2),
        level: 0,
      });
    });

    this.steps = steps.filter((buffer): buffer is AudioBuffer => buffer !== undefined);
    this.chops = chops.filter((buffer): buffer is AudioBuffer => buffer !== undefined);

    const takes = work.filter((buffer): buffer is AudioBuffer => buffer !== undefined);
    if (manifest.construction && takes.length > 0) {
      const gain = this.ctx.createGain();
      gain.gain.value = 0;
      gain.connect(this.buses.ambience);
      this.work = {
        // Kształt wpisu tła, żeby głośność szła tą samą drogą co las.
        entry: { key: 'construction', file: '', radius: manifest.construction.radius, gain: manifest.construction.gain },
        gain,
        loop: new CrossfadeLoop(this.ctx, takes, gain, 2),
        level: 0,
      };
    }
    this.loaded = true;
    if (this.unlocked) this.startBeds();
  }

  /** Z ilu pól słychać plac budowy; zero, gdy nie ma czego słuchać. */
  get constructionRadius(): number {
    return this.work?.entry.radius ?? 0;
  }

  /** Jak blisko najbliższy plac budowy, od 0 do 1 — ta sama rampa co tło. */
  setConstruction(level: number): void {
    const bed = this.work;
    if (!bed) return;
    bed.level = Math.min(1, Math.max(0, level));
    bed.gain.gain.setTargetAtTime(bed.level * (bed.entry.gain ?? 1), this.ctx.currentTime, AMBIENCE_GLIDE);
  }

  /** The ambience keys this engine actually has a sound for. */
  get ambienceKeys(): string[] {
    return [...this.beds.keys()];
  }

  /** How many rings around the player one bed listens to. */
  radiusOf(key: string): number {
    return this.beds.get(key)?.entry.radius ?? 0;
  }

  /**
   * How close the player is to what a bed is about, from 0 to 1.
   *
   * Approached rather than set: walking one cell can take a ring from half wood
   * to none, and a bed that snapped would be a sound switching off mid-step.
   * `setTargetAtTime` is an exponential approach, which is what "fades in as you
   * get closer" sounds like when it is done well.
   */
  setAmbience(key: string, level: number): void {
    const bed = this.beds.get(key);
    if (!bed) return;

    bed.level = Math.min(1, Math.max(0, level));
    const target = bed.level * (bed.entry.gain ?? 1);
    bed.gain.gain.setTargetAtTime(target, this.ctx.currentTime, AMBIENCE_GLIDE);
  }

  /**
   * One footstep.
   *
   * A variant is picked rather than a loop played, and never the same one twice
   * running: at three steps a second the repetition is the first thing anybody
   * hears. Pitch and level are nudged on top, so even a repeat is not identical.
   */
  step(): void {
    if (!this.unlocked || this.steps.length === 0) return;
    this.lastStep = this.variant(this.steps, this.lastStep, 1);
  }

  /**
   * Jedno uderzenie topora, `level` od 0 do 1 — ciszej, im dalej wycinający.
   *
   * Ta sama zasada co przy krokach i ta sama funkcja: wariant nigdy dwa razy
   * z rzędu, z drobnym rozstrojeniem na wierzchu.
   */
  chop(level = 1): void {
    if (!this.unlocked || this.chops.length === 0 || level <= 0) return;
    this.lastChop = this.variant(this.chops, this.lastChop, Math.min(1, level));
  }

  /** Zagraj jeden z wariantów, innym niż ostatni. Oddaje, który zagrał. */
  private variant(buffers: AudioBuffer[], last: number, level: number): number {
    let at = Math.floor(Math.random() * buffers.length);
    if (buffers.length > 1 && at === last) {
      at = (at + 1) % buffers.length;
    }

    const source = this.ctx.createBufferSource();
    source.buffer = buffers[at];
    source.playbackRate.value = 1 + (Math.random() * 2 - 1) * STEP_PITCH;

    const gain = this.ctx.createGain();
    gain.gain.value = level * (1 - Math.random() * STEP_LEVEL);

    source.connect(gain).connect(this.buses.effects);
    source.start();
    source.onended = () => {
      gain.disconnect();
      source.disconnect();
    };
    return at;
  }

  dispose(): void {
    this.music?.stop();
    for (const bed of this.beds.values()) bed.loop?.stop();
    this.work?.loop?.stop();
    void this.ctx.close().catch(() => undefined);
  }

  /** Everything that runs for as long as the game does, once it is allowed to. */
  private startBeds(): void {
    if (!this.loaded) return;

    this.music?.start();
    for (const bed of this.beds.values()) {
      bed.loop?.start();
      // The ground may already have been read while the sound was still locked.
      this.setAmbience(bed.entry.key, bed.level);
    }
    this.work?.loop?.start();
    if (this.work) this.setConstruction(this.work.level);
  }
}
